import { Box, Icon } from "@outschool/backpack";
import { faGift } from "@outschool/icons";
import {
  Trans,
  renderCurrencyString,
  renderPercentageDiscountString,
  useTranslation
} from "@outschool/localization";
import { formatDate } from "@outschool/time";
import {
  usePageAlerts,
  useRetentionExperiment
} from "@outschool/ui-components-website";
import React, { useMemo } from "react";

const useShouldShowUserCouponCodePageAlert = () => {
  const { retentionExperimentCouponCode, loading, error } =
    useRetentionExperiment();

  return useMemo(() => {
    if (!loading && !error && retentionExperimentCouponCode) {
      const percentOff = retentionExperimentCouponCode.coupon?.percentOff ?? 0;
      const maxAmountCentsPerUser =
        retentionExperimentCouponCode.coupon?.maxAmountCentsPerUse ?? 0;
      const formattedDate = retentionExperimentCouponCode?.expiresAt
        ? formatDate(retentionExperimentCouponCode.expiresAt)
        : "";

      return {
        discount: percentOff
          ? renderPercentageDiscountString({ percentOff })
          : renderCurrencyString({ priceInCents: maxAmountCentsPerUser }),
        couponCode: retentionExperimentCouponCode?.code ?? "",
        expiresAt: formattedDate
      };
    } else {
      return {
        discount: "",
        couponCode: "",
        expiresAt: ""
      };
    }
  }, [loading, error, retentionExperimentCouponCode]);
};

export const useUserCouponCodePageAlert = () => {
  const { t } = useTranslation(
    "client\\components\\alerts\\UserCouponCodePageAlert"
  );

  const { addPageAlert, removePageAlert } = usePageAlerts();

  const { discount, couponCode, expiresAt } =
    useShouldShowUserCouponCodePageAlert();

  const paymentFailurePageAlertId = `retention-discount-banner-${couponCode}`;

  React.useEffect(() => {
    if (discount) {
      addPageAlert({
        id: paymentFailurePageAlertId,
        severity: "success",
        title: t(`{{currentDiscount}} off your next class!`, {
          currentDiscount: discount
        }),
        content: (
          <Box>
            <Trans t={t}>
              Apply Code <b>{{ couponCode }}</b> at checkout before {expiresAt}.
            </Trans>
          </Box>
        ),
        dismissable: true,
        persistant: true,
        icon: <Icon icon={faGift} color="success" size="medium" />
      });
    } else {
      removePageAlert(paymentFailurePageAlertId);
    }
  }, [
    couponCode,
    discount,
    expiresAt,
    paymentFailurePageAlertId,
    addPageAlert,
    removePageAlert,
    t
  ]);
};
